import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { compareAsc } from 'date-fns';
import { z } from 'zod';

import { DocumentPreview } from '@/components/pages/documents/document-preview';
import { LoadError } from '@/components/ui/load-error';
import { Loading } from '@/components/ui/old/Loading';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import {
  type Document,
  type MissingDocument,
} from '@/modules/documents/domain/entities/document';
import { DocumentTypeSchema } from '@/modules/documents/domain/entities/document-type';
import { siteLabel } from '@/utils/format/customer';

import { DocumentEditDialog } from '../../components/pages/site-documents/document-edit-dialog';
import { DocumentUploadDialog } from '../../components/pages/site-documents/document-upload-dialog';
import { GetAllDocuments } from '../../queries/get-all-documents';

import { DocumentsFilters } from './documents-filters';
import { DocumentsPagination } from './documents-pagination';
import { DocumentsTable } from './documents-table';
import { useFilters } from './router/hooks/use-filters';
import { usePagination } from './router/hooks/use-pagination';
import { usePathParams } from './router/hooks/use-path-params';
import { SortOrder, useSort } from './router/hooks/use-sort';
import { MissingDocumentsDisclosure } from './ui/components/missing-documents-disclosure';

const PathParamsSchema = z.object({ siteId: z.string().optional() });
const FiltersSchema = z.object({
  search: z.string().optional(),
  type: DocumentTypeSchema.optional(),
});

type DocumentDialogState = {
  loading?: boolean;
} & (
  | { document: Document; type: 'edit' }
  | { document?: MissingDocument; type: 'upload' }
);

export function SiteDocumentsPage() {
  const { siteId } = useParams<{ siteId?: string }>();
  const { asset, vehicle } = useCurrentAsset();
  const assetName = asset
    ? siteLabel(asset.fullFrontendId)
    : vehicle?.fullFrontendId;

  const pathParams = usePathParams(PathParamsSchema);
  const [dialogState, setDialogState] = useState<DocumentDialogState | null>(
    null,
  );

  const allDocuments = GetAllDocuments.useQuery({
    ...pathParams,
    siteId,
  });

  const [filteredDocuments, filters] = useFilters(
    allDocuments.data?.documents ?? [],
    FiltersSchema,
    {
      search: (document, value) =>
        document.name.toLowerCase().includes(value.toLowerCase()) ||
        document.id.toLowerCase() === value.toLowerCase(),
      type: (document, value) => document.type === value,
    },
  );

  const [sortedDocuments, sort] = useSort(
    filteredDocuments,
    {
      uploaded_by(a, b) {
        return a.uploadedBy.displayName.localeCompare(b.uploadedBy.displayName);
      },
      name(a, b) {
        return a.name.localeCompare(b.name);
      },
      last_uploaded(a, b) {
        return compareAsc(a.updatedAt, b.updatedAt);
      },
      type(a, b) {
        return a.type.localeCompare(b.type);
      },
      required(a, b) {
        if (a.required && b.required) {
          return 0;
        } else if (a.required) {
          return -1;
        } else {
          return 1;
        }
      },
    },
    'last_uploaded',
    SortOrder.DESC,
  );

  const [paginatedDocuments, pagination] = usePagination(sortedDocuments);

  const [{ id }, queryParams] = useQueryParams({
    id: {
      key: 'id',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  if (allDocuments.isLoading) {
    return <Loading />;
  }

  if (allDocuments.isError) {
    return <LoadError title="An error occurred while loading documents" />;
  }

  const previewDocument = id
    ? allDocuments.data.documents.find((d) => d.id === id)
    : undefined;

  return (
    <div
      data-testid="documents-page"
      className="mx-auto max-w-screen-xl space-y-8 p-8 xl:py-16"
    >
      {siteId ? (
        <div>
          <h1 className="mb-2 text-xl font-bold text-zinc-950">
            {assetName ? `Site Documents for ${assetName}` : 'Site Documents'}
          </h1>
          <p className="text-lg text-zinc-500">
            Site-specific documents can be uploaded here.
          </p>
        </div>
      ) : (
        <div>
          <h1 className="mb-2 text-xl font-bold text-zinc-950">Documents</h1>
          <p className="text-lg text-zinc-500">
            Global documents can be uploaded here. You can find site-specific
            documents on the Documents page of that site.
          </p>
        </div>
      )}
      {allDocuments.data.missingDocuments.length > 0 ? (
        <MissingDocumentsDisclosure
          documents={allDocuments.data.missingDocuments}
          onItemClick={(document: MissingDocument) =>
            setDialogState({ document, type: 'upload' })
          }
        />
      ) : null}
      <DocumentsFilters
        filters={filters}
        onUploadClick={() => setDialogState({ type: 'upload' })}
      />
      <DocumentsTable
        documents={paginatedDocuments}
        loading={allDocuments.isLoading}
        siteId={siteId}
        onEditClick={(document: Document) =>
          setDialogState({ document, type: 'edit' })
        }
        sort={sort}
      />
      <DocumentsPagination pagination={pagination} />
      {dialogState ? (
        <DocumentDialog
          {...dialogState}
          siteId={siteId}
          open
          onClose={() => setDialogState(null)}
        />
      ) : null}
      <DocumentPreview
        document={previewDocument}
        onClose={() => queryParams.replace({ id: undefined })}
      />
    </div>
  );
}

type DocumentDialogProps = {
  onClose: () => void;
  siteId?: string;
  open?: boolean;
} & (
  | { document?: MissingDocument; type: 'upload' }
  | { document: Document; type: 'edit' }
);
function DocumentDialog({ document, type, ...props }: DocumentDialogProps) {
  if (type === 'edit') {
    return <DocumentEditDialog document={document} {...props} />;
  }

  return <DocumentUploadDialog template={document} {...props} />;
}
